import type { AmpLibraryFoldersQuery } from "src/graphql";

export type FileFolder = AmpLibraryFoldersQuery["ampFileFolders"][number];

/** Stands in for a folder id to show the viewer's favorites. Real folder ids are uuids, so it never matches one. */
export const FAVORITES_FOLDER_ID = "favorites";
/** Stands in for a folder id to show the files the viewer created. */
export const MY_FILES_FOLDER_ID = "mine";
/** Stands in for a folder id to show Drafts, where new files start. */
export const DRAFTS_FOLDER_ID = "drafts";
/** Stands in for a folder id to select the Library's New view in its sidebar; the view itself has its own path. */
export const NEW_FOLDER_ID = "new";

/** Every folder with its full path, such as "Launch / Ads", in path order so a subfolder follows its parent. */
export function folderPaths(folders: FileFolder[]) {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const pathOf = (folder: FileFolder): FileFolder[] => {
    const path: FileFolder[] = [];
    const seen = new Set<string>();
    for (
      let at: FileFolder | undefined = folder;
      at && !seen.has(at.id);
      at = at.parentId ? byId.get(at.parentId) : undefined
    ) {
      seen.add(at.id);
      path.unshift(at);
    }
    return path;
  };
  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: "base",
  });
  return folders
    .map((folder) => ({ folder, path: pathOf(folder) }))
    .sort((a, b) => {
      for (let i = 0; i < Math.min(a.path.length, b.path.length); i++) {
        const left = a.path[i];
        const right = b.path[i];
        if (!left || !right) break;
        // Same-named siblings stay apart by id, so each keeps its own subfolders beneath it.
        const order =
          collator.compare(left.name, right.name) ||
          left.id.localeCompare(right.id);
        if (order) return order;
      }
      return a.path.length - b.path.length;
    })
    .map(({ folder, path }) => ({
      folder,
      path: path.map((at) => at.name).join(" / "),
    }));
}

/** The folder with the given id and every folder anywhere under it. */
export function folderAndSubfolderIds(folders: FileFolder[], id: string) {
  const found = new Set([id]);
  // Repeats until a pass adds nothing, so a subfolder listed before its parent is still found.
  for (let size = 0; size !== found.size; ) {
    size = found.size;
    for (const folder of folders) {
      if (folder.parentId && found.has(folder.parentId)) found.add(folder.id);
    }
  }
  return found;
}

/**
 * The folder tree as rows in display order. A folder shows its subfolders when it is open
 * or when the selected folder is below it, as in Ad Studio.
 */
export function folderRows(
  folders: FileFolder[],
  open: ReadonlySet<string>,
  selectedFolderId: string | null,
) {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const ancestors = (folder: FileFolder) => {
    const found: string[] = [];
    for (
      let parentId = folder.parentId;
      parentId && byId.has(parentId) && !found.includes(parentId);
      parentId = byId.get(parentId)?.parentId ?? null
    )
      found.push(parentId);
    return found;
  };
  const selected = selectedFolderId ? byId.get(selectedFolderId) : undefined;
  const aboveSelected = new Set(selected ? ancestors(selected) : []);
  const isOpen = (id: string) => open.has(id) || aboveSelected.has(id);
  return folderPaths(folders).flatMap(({ folder }) => {
    const above = ancestors(folder);
    if (!above.every(isOpen)) return [];
    return [{ folder, depth: above.length, isOpen: isOpen(folder.id) }];
  });
}
